import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CustomerNoteInput } from '../../../types/customer'
import type { CustomerAdapter } from '../adapters/types'

// The legacy timeline UI loaded the full feed on demand with a `limit`
// parameter — there's no cursor in the response, so this is a plain
// useQuery (not useInfiniteQuery). When the UI lands and we need
// "load more", swap in `useInfiniteQuery` once the backend exposes a
// cursor/offset shape.

export const customerTimelineQueryKey = (
  storeType: string,
  storeId: string,
  customerId: string,
) => ['customer-timeline', storeType, storeId, customerId] as const

export const useCustomerTimeline = (
  adapter: CustomerAdapter,
  customerId: string | undefined,
  limit = 20,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [
      ...customerTimelineQueryKey(
        adapter.storeType,
        adapter.storeId,
        customerId ?? '',
      ),
      limit,
    ] as const,
    queryFn: () => adapter.listTimeline(customerId as string, limit),
    enabled: (options?.enabled ?? true) && Boolean(customerId),
  })

export const useAddCustomerNote = (
  adapter: CustomerAdapter,
  customerId: string,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (note: CustomerNoteInput) => adapter.addNote(customerId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customerTimelineQueryKey(
          adapter.storeType,
          adapter.storeId,
          customerId,
        ),
      })
    },
  })
}

export const useDeleteCustomerNote = (
  adapter: CustomerAdapter,
  customerId: string,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (noteId: string) => adapter.deleteNote(customerId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customerTimelineQueryKey(
          adapter.storeType,
          adapter.storeId,
          customerId,
        ),
      })
    },
  })
}
